import { Trans } from "@lingui/react/macro"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, notFound } from "@tanstack/react-router"
import { useMemo } from "react"
import { shelfCodesQueryOptions } from "@/features/bill-of-materials/hooks/useShelfCodes"
import { ShelfCodeDetail } from "@/features/list-of-materials/components/shelf-codes/ShelfCodeDetail"
import { TextLink } from "@/features/list-of-materials/components/ui/Button"
import { codingUsage } from "@/features/list-of-materials/core/coding"
import { useCanEditLom } from "@/features/list-of-materials/hooks/useCanEditLom"
import {
  pickableArticlesQueryOptions,
  lomCodingQueryOptions,
} from "@/features/list-of-materials/hooks/useLomData"
import {
  lomRecentChangesQueryOptions,
  lomVersionsQueryOptions,
  useSelectedLomVersion,
} from "@/features/list-of-materials/hooks/useLomVersion"

export const Route = createFileRoute("/(authenticated)/list-of-materials/shelf-codes/$shelfCodeId")(
  {
    loaderDeps: ({ search }) => ({ version: search.version }),
    loader: async ({ context, deps, params }) => {
      const { selected } = await context.queryClient.ensureQueryData(
        lomVersionsQueryOptions(deps.version),
      )
      if (!selected) return
      void context.queryClient.prefetchQuery(lomCodingQueryOptions(selected.id))
      const rows = await context.queryClient.ensureQueryData(shelfCodesQueryOptions(selected.id))
      if (!rows.some((r) => r.id === params.shelfCodeId)) throw notFound()
    },
    component: ShelfCodePage,
  },
)

function ShelfCodePage() {
  const { shelfCodeId } = Route.useParams()
  const { version, versionSearch } = useSelectedLomVersion()
  const canEdit = useCanEditLom()
  const { data: rows } = useSuspenseQuery(shelfCodesQueryOptions(version.id))
  const { data: changes } = useQuery(lomRecentChangesQueryOptions(version.id, shelfCodeId))
  const { data: coding } = useQuery(lomCodingQueryOptions(version.id))
  const { data: articles = [] } = useQuery({
    ...pickableArticlesQueryOptions(version.id),
    enabled: canEdit,
  })
  const usage = useMemo(() => codingUsage(coding?.templates ?? []), [coding])
  const row = rows.find((r) => r.id === shelfCodeId)

  return (
    <>
      <TextLink
        to="/list-of-materials/shelf-codes"
        search={versionSearch}
        className="mb-4 inline-flex items-center gap-1 text-[15px]"
      >
        ‹ <Trans>Shelf codes</Trans>
      </TextLink>
      {row ? (
        <ShelfCodeDetail
          row={row}
          allCodes={rows}
          configVersionId={version.id}
          version={versionSearch.version}
          usedInCells={usage.get(row.code.toUpperCase()) ?? 0}
          changes={changes}
          articles={articles}
          canEdit={canEdit}
        />
      ) : (
        <p className="text-lom-ink-3">
          <Trans>This shelf code no longer exists.</Trans>
        </p>
      )}
    </>
  )
}
